import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_CRON } from '../../../../environments/environment';
import {
  IResumenDownlinksLuminaria,
  IResumenDownlinksSistema,
} from 'modelos/src';

// Franja horaria EN CURSO (acumulador Redis en vivo) que el cron aún no cerró.
// Se mergea sobre las franjas persistidas para tiempo real (0 lag).
export interface FranjaActual {
  periodoInicio: string;
  periodoFin: string;
  sistema: { idCliente?: string; resumen: IResumenDownlinksSistema }[];
  luminaria: {
    idCliente?: string;
    deveui: string;
    resumen: IResumenDownlinksLuminaria;
  }[];
}

export interface MetricaSample {
  labels: Record<string, string | number>;
  value: number;
}

export interface MetricaItem {
  type: 'counter' | 'gauge';
  help: string;
  samples: MetricaSample[];
}

// Config del secuenciador de downlinks (envío ordenado 1-en-vuelo por device).
// Incluye los parámetros de cola/rate-limit/envío (antes agrupados como
// 'orquestador'): el processor + rate-limiter sirven al secuenciador.
export interface ConfigSecuenciador {
  deadlineAckSec: number;
  deadlineEcoSec: number;
  maxReintentosPaso: number;
  planTtlSec: number;
  // Margen (s) que se suma al timeout del paso al programar su control de
  // respaldo, para no reintentar mientras aún puede llegar la respuesta.
  sendBufferSec: number;
  // Envío / cola / rate-limit.
  queueName: string;
  concurrency: number;
  rateGlobalPerSec: number;
  ratePerGwMs: number;
  rateNoGwSec: number;
  expiresMinManual: number;
  expiresMinReconciliacion: number;
}

// Circuit breaker del plan. No está siempre activo: se ARMA por device cuando un
// plan completo termina sin una sola señal y se CONSUME al armar el siguiente,
// así que dos tandas seguidas nunca pueden cortarse.
export interface ConfigBreaker {
  pasosSinSenal: number;
  mudoTtlSec: number;
}

// Control de congestión: cupo de planes activos simultáneos por gateway, movido
// por AIMD con la tasa de fallo del propio gateway.
export interface ConfigCongestion {
  enabled: boolean;
  cupoInicial: number;
  cupoMin: number;
  cupoMax: number;
  umbralSubirPct: number;
  umbralBajarPct: number;
  umbralProteccionPct: number;
  minMuestras: number;
  ventanaSeg: number;
  memoriaSeg: number;
}

export interface ConfigReconciliador {
  enabled: boolean;
  cron: string;
  batchSize: number;
  backoffIntentos: number;
  bloqueoHoras: number;
}

export interface ConfigReintentosDescartados {
  enabled: boolean;
  cron: string;
  ventanaMin: number;
}

export interface ConfigConsultaConfig {
  enabled: boolean;
  cron: string;
  batchSize: number;
  backoffIntentos: number;
  bloqueoHoras: number;
  minIntervaloHoras: number;
}

export interface ConfigBarridoComandos {
  enabled: boolean;
  cron: string;
  timeoutMs: number;
}

// Cron que persiste las métricas por franja horaria a IResumenDatos.
export interface ConfigMetricasCron {
  enabled: boolean;
  cron: string;
  redisTtlSeg: number;
}

export interface MetricasEstado {
  metricas: Record<string, MetricaItem>;
  config: {
    secuenciador: ConfigSecuenciador;
    reconciliador: ConfigReconciliador;
    reintentosDescartados: ConfigReintentosDescartados;
    consultaConfig: ConfigConsultaConfig;
    barridoComandos?: ConfigBarridoComandos;
    metricasCron?: ConfigMetricasCron;
    breaker?: ConfigBreaker;
    congestion?: ConfigCongestion;
  };
}

@Injectable({ providedIn: 'root' })
export class MetricasCronService {
  private http = inject(HttpClient);

  getEstado(): Promise<MetricasEstado> {
    return firstValueFrom(
      this.http.get<MetricasEstado>(`${API_CRON}/metrics/estado`),
    );
  }

  // Franja horaria en curso (Redis en vivo) para real-time.
  getFranjaActual(): Promise<FranjaActual> {
    return firstValueFrom(
      this.http.get<FranjaActual>(`${API_CRON}/metrics/franja-actual`),
    );
  }
}
